(() => {
  const rootSelector = '[data-bp-bpc-fast-shop]';
  const analyticsKeys = new Set([
    'product_handle',
    'variant_id',
    'category',
    'source',
    'error_code',
  ]);

  if (window.BPBPCFastShop) {
    window.BPBPCFastShop.initialize(document);
    return;
  }

  const instances = new WeakMap();
  const instancesBySectionId = new Map();

  const filteredPayload = (payload) => Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => analyticsKeys.has(key) && value !== '' && value != null),
  );

  class BPCFastShop {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.liveRegion = root.querySelector('[data-bp-bpc-live]');
      this.abortController = new AbortController();
      this.intersectionObserver = null;
      this.unsubscribers = [];
      this.pendingAdd = null;
      this.modalContext = null;
      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleRootSubmit = this.handleRootSubmit.bind(this);
      this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this);
    }

    initialize() {
      const { signal } = this.abortController;
      this.root.addEventListener('click', this.handleRootClick, { signal });
      this.root.addEventListener('submit', this.handleRootSubmit, { capture: true, signal });
      document.addEventListener('submit', this.handleDocumentSubmit, { capture: true, signal });
      this.observeProducts();
      this.subscribeToCart();
      this.root.classList.add('is-initialized');
    }

    handleRootClick(event) {
      const categoryLink = event.target.closest('[data-bp-bpc-category]');
      if (categoryLink && this.root.contains(categoryLink)) {
        this.emit('bp:bpc_category_select', {
          category: categoryLink.dataset.category,
          source: 'bpc-fast-shop',
        });
      }

      const quickAddButton = event.target.closest('quick-add button');
      if (quickAddButton && this.root.contains(quickAddButton)) {
        this.modalContext = this.contextFromCard(quickAddButton.closest('[data-bp-bpc-product]'));
      }
    }

    handleRootSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const card = form.closest('[data-bp-bpc-product]');
      if (!card || !this.root.contains(card)) return;
      this.prepareSubmission(event, form, this.contextFromCard(card));
    }

    handleDocumentSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('.wt__quick-buy') || !this.modalContext) return;
      this.prepareSubmission(event, form, this.modalContext);
    }

    prepareSubmission(event, form, context) {
      const variantInput = form.querySelector('[name="id"]');
      const variantId = variantInput?.value?.trim();

      if (!variantId) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.reportError({ ...context, error_code: 'missing_variant' });
        const optionControl = form.closest('.wt__quick-buy')?.querySelector('variant-options label');
        optionControl?.focus();
        return;
      }

      if (this.pendingAdd) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      this.pendingAdd = {
        ...context,
        variant_id: variantId,
      };
    }

    contextFromCard(card) {
      return {
        product_handle: card?.dataset.productHandle || '',
        category: card?.dataset.analyticsCategory || 'bestseller',
        source: 'bpc-fast-shop',
      };
    }

    subscribeToCart() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.handleCartUpdate(event)),
        subscribe(PUB_SUB_EVENTS.cartError, (event) => this.handleCartError(event)),
        subscribe(PUB_SUB_EVENTS.quickBuyDrawerClose, () => {
          if (!this.pendingAdd) this.modalContext = null;
        }),
      );
    }

    handleCartUpdate(event) {
      if (!this.matchesPendingVariant(event?.productVariantId)) return;

      this.emit('bp:bpc_quick_add', this.pendingAdd);
      this.announce(this.root.dataset.successMessage);
      this.pendingAdd = null;
      this.modalContext = null;
    }

    handleCartError(event) {
      if (!this.matchesPendingVariant(event?.productVariantId)) return;

      const errorCode = event?.errorCode || this.classifyCartError(event);
      this.reportError({ ...this.pendingAdd, error_code: errorCode });
      this.pendingAdd = null;
    }

    matchesPendingVariant(variantId) {
      if (!this.pendingAdd) return false;
      return String(variantId || '') === String(this.pendingAdd.variant_id);
    }

    classifyCartError(event) {
      const message = `${event?.errors || ''} ${event?.message || ''}`.toLowerCase();
      if (message.includes('sold out') || message.includes('esaurit') || message.includes('unavailable')) {
        return 'sold_out';
      }
      return 'cart_error';
    }

    reportError(payload) {
      this.emit('bp:bpc_quick_add_error', payload);
      this.announce(this.root.dataset.errorMessage);
    }

    announce(message) {
      if (!this.liveRegion || !message) return;
      this.liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        this.liveRegion.textContent = message;
      });
    }

    observeProducts() {
      const products = Array.from(this.root.querySelectorAll('[data-bp-bpc-product]'));
      if (!products.length) return;

      if (typeof IntersectionObserver === 'undefined') {
        products.forEach((product) => this.emitProductView(product));
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.emitProductView(entry.target);
            this.intersectionObserver?.unobserve(entry.target);
          });
        },
        { threshold: 0.4 },
      );
      products.forEach((product) => this.intersectionObserver.observe(product));
    }

    emitProductView(product) {
      if (product.dataset.bpBpcViewed === 'true') return;
      product.dataset.bpBpcViewed = 'true';
      this.emit('bp:bpc_product_view', this.contextFromCard(product));
    }

    emit(name, payload) {
      this.root.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: filteredPayload(payload),
        }),
      );
    }

    handleBlockSelect(blockId) {
      const block = this.root.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
      if (!block) return;
      block.classList.add('is-editor-selected');
      block.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }

    handleBlockDeselect(blockId) {
      if (blockId) {
        this.root.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`)?.classList.remove('is-editor-selected');
        return;
      }
      this.root.querySelectorAll('.is-editor-selected').forEach((block) => block.classList.remove('is-editor-selected'));
    }

    destroy() {
      this.abortController.abort();
      this.intersectionObserver?.disconnect();
      this.unsubscribers.forEach((unsubscribe) => unsubscribe?.());
      this.unsubscribers = [];
      this.pendingAdd = null;
      this.modalContext = null;
      this.root.classList.remove('is-initialized');
      instances.delete(this.root);
      instancesBySectionId.delete(this.sectionId);
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container instanceof Element && container.matches(rootSelector)) roots.push(container);
    container.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const initialize = (container = document) => {
    rootsWithin(container).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new BPCFastShop(root);
      instances.set(root, instance);
      instancesBySectionId.set(instance.sectionId, instance);
      instance.initialize();
    });
  };

  const destroy = (container) => {
    rootsWithin(container).forEach((root) => instances.get(root)?.destroy());
  };

  const instanceForEvent = (event) => {
    const directRoot = event.target?.closest?.(rootSelector) || event.target?.querySelector?.(rootSelector);
    if (directRoot && instances.has(directRoot)) return instances.get(directRoot);
    return instancesBySectionId.get(event.detail?.sectionId);
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => instanceForEvent(event)?.handleBlockSelect(event.detail?.blockId));
  document.addEventListener('shopify:block:deselect', (event) => instanceForEvent(event)?.handleBlockDeselect(event.detail?.blockId));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  window.BPBPCFastShop = Object.freeze({ initialize, destroy });
})();
